import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Spinner } from 'react-bootstrap';
import Cookies from 'js-cookie';
import { getShortChatSession, clearShortChatSession, formatShortChatMessages } from '../../chatHistory';
import { supabase } from '../../supabaseClient';

// Strip hidden tool context from messages (used for AI context but not display)
const stripToolContext = (text) => {
  if (!text) return '';
  return text.replace(/\n?<!-- TOOL_CONTEXT:[\s\S]*?-->/g, '').trim();
};

// Simple markdown-like formatter for chat messages
const formatMessageContent = (text) => {
  if (!text) return '';
  
  // First strip hidden tool context
  let formatted = stripToolContext(text)
    // Bold: **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    // Markdown links: [text](url)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Plain URLs (not already in an anchor tag)
    .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>')
    // Code: `code`
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br/>');
  
  // Convert bullet lists (- item or * item)
  formatted = formatted.replace(/(?:^|<br\/>)\s*[-*]\s+(.+?)(?=<br\/>|$)/g, '<li>$1</li>');
  if (formatted.includes('<li>')) {
    formatted = formatted.replace(/(<li>.*<\/li>)/g, '<ul>$1</ul>');
    // Clean up consecutive ul tags
    formatted = formatted.replace(/<\/ul><ul>/g, '');
  }
  
  return formatted;
};

// Parse user message to extract attachment URL and text
const parseUserMessage = (content) => {
  if (!content) return { text: '', attachmentUrl: null };
  
  // Match [Attachment: URL] pattern
  const attachmentMatch = content.match(/\[Attachment:\s*(https?:\/\/[^\]]+)\]/i);
  
  if (attachmentMatch) {
    const attachmentUrl = attachmentMatch[1];
    const text = content.replace(attachmentMatch[0], '').trim();
    return { text, attachmentUrl };
  }
  
  // Also check for [📎 filename] pattern from local preview
  const fileMatch = content.match(/\[📎\s*([^\]]+)\]/);
  
  return { text: content, attachmentUrl: null, fileName: fileMatch ? fileMatch[1] : null };
};

// Check if URL is an image
const isImageUrl = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(url);
};

// Get file type from URL
const getFileTypeFromUrl = (url) => {
  if (!url) return 'file';
  if (/\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(url)) return 'image';
  if (/\.pdf(\?.*)?$/i.test(url)) return 'pdf';
  if (/\.(xlsx|xls|csv)(\?.*)?$/i.test(url)) return 'spreadsheet';
  return 'file';
};

const TaskAssistantChat = forwardRef(({
  isDarkMode,
  onToggle,
  showInput = true,
  externalInput,
  onExternalInputChange,
  onExternalSend,
  externalLoading,
  // Contextual chat props - now uses 'tasks' | 'notes' | 'lists'
  contextType = 'tasks', // 'tasks' | 'notes' | 'lists'
  contextId = null,
  categoryPath = [],
  userId = null,
  onItemsUpdated = null
}, ref) => {
  const [isMinimized, setIsMinimized] = useState(false);

  const handleToggle = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    if (onToggle) onToggle(newState);
  };

  // Map legacy contextType to new format
  const chatType = contextType === 'task_list' ? 'tasks' : contextType === 'note' ? 'notes' : contextType === 'list' ? 'lists' : contextType;
  const typeLabel = chatType === 'tasks' ? 'tasks' : chatType === 'notes' ? 'notes' : chatType === 'reminders' ? 'reminders' : chatType === 'scheduled_tasks' ? 'scheduled tasks' : chatType === 'ideas' ? 'ideas' : 'lists';

  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?` }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [statusMessage, setStatusMessage] = useState('Thinking');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Use external state if provided, otherwise use internal state
  const currentInput = externalInput !== undefined ? externalInput : input;
  const setCurrentInput = onExternalInputChange || setInput;
  const currentLoading = externalLoading !== undefined ? externalLoading : isLoading;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isMinimized]);

  // Get effective user ID
  const effectiveUserId = userId || Cookies.get('user_id') || 'anonymous';

  // Load existing chat session from database when context changes
  useEffect(() => {
    const loadSession = async () => {
      setIsLoadingSession(true);

      try {
        console.log('[TaskAssistantChat] Loading session for:', chatType, effectiveUserId);

        const { session, messages: dbMessages } = await getShortChatSession(effectiveUserId, chatType);

        if (session && dbMessages.length > 0) {
          // Format messages for display
          const formattedMessages = formatShortChatMessages(dbMessages);

          // Add greeting at the beginning if not present
          if (formattedMessages.length > 0 && formattedMessages[0].role !== 'assistant') {
            formattedMessages.unshift({
              role: 'assistant',
              content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?`
            });
          }

          setMessages(formattedMessages.length > 0 ? formattedMessages : [
            { role: 'assistant', content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?` }
          ]);
          setSessionId(session.id);
          console.log('[TaskAssistantChat] Loaded session:', session.id, 'with', formattedMessages.length, 'messages');
        } else {
          // No saved session, start fresh
          setMessages([
            { role: 'assistant', content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?` }
          ]);
          setSessionId(null);
          console.log('[TaskAssistantChat] No existing session found, starting fresh');
        }
      } catch (error) {
        console.error('[TaskAssistantChat] Error loading session:', error);
        setMessages([
          { role: 'assistant', content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?` }
        ]);
        setSessionId(null);
      } finally {
        setIsLoadingSession(false);
      }
    };

    if (effectiveUserId && effectiveUserId !== 'anonymous') {
      loadSession();
    } else {
      setIsLoadingSession(false);
    }
  }, [chatType, effectiveUserId, typeLabel]);

  // File upload handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'text/csv', 'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      alert('Please select an image (JPG, PNG, GIF, WEBP), PDF, or CSV/Excel file.');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview({ type: 'image', url: reader.result, name: file.name });
      };
      reader.readAsDataURL(file);
    } else {
      // For non-image files, just show the name
      const icon = file.type === 'application/pdf' ? 'bi-file-pdf' : 'bi-file-earmark-spreadsheet';
      setFilePreview({ type: 'file', icon, name: file.name });
    }

    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  // Upload file to Supabase storage (using existing Storage bucket)
  const uploadFileToStorage = async (file, odUid) => {
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    const fileName = `${timestamp}-${randomId}.${fileExt}`;
    const filePath = `${odUid}/short-chat-attachments/${fileName}`;

    const { data, error } = await supabase.storage
      .from('Storage')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('Storage')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  const handleSend = useCallback(async () => {
    if ((!currentInput.trim() && !selectedFile) || currentLoading) return;

    // Create user message with file info if present
    const userMessageContent = selectedFile
      ? `${currentInput.trim() || 'Analyze this file'} [📎 ${selectedFile.name}]`
      : currentInput;
    const userMessage = {
      role: 'user',
      content: userMessageContent,
      file: filePreview
    };
    setMessages(prev => [...prev, userMessage]);
    const messageText = currentInput.trim() || (selectedFile ? `Please analyze this ${selectedFile.type.includes('pdf') ? 'PDF' : selectedFile.type.includes('image') ? 'image' : 'file'}: ${selectedFile.name}` : '');

    // Store file reference before clearing
    const fileToUpload = selectedFile;

    // Clear input and file
    setCurrentInput('');
    setSelectedFile(null);
    setFilePreview(null);

    if (onExternalSend) {
      onExternalSend(messageText);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      // Upload file if selected
      let attachmentUrl = null;
      if (fileToUpload) {
        try {
          attachmentUrl = await uploadFileToStorage(fileToUpload, effectiveUserId);
          console.log('[TaskAssistantChat] File uploaded:', attachmentUrl);
          
          // Update the user message with the Supabase URL for persistence
          setMessages(prev => {
            const updated = [...prev];
            const lastUserMsgIndex = updated.length - 1;
            if (updated[lastUserMsgIndex] && updated[lastUserMsgIndex].role === 'user') {
              // Add attachment URL to message content (for parsing later)
              updated[lastUserMsgIndex] = {
                ...updated[lastUserMsgIndex],
                content: `[Attachment: ${attachmentUrl}]\n\n${messageText}`,
                attachmentUrl: attachmentUrl, // Also store directly for easy access
              };
            }
            return updated;
          });
        } catch (uploadError) {
          console.error('[TaskAssistantChat] File upload failed:', uploadError);
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: 'Sorry, failed to upload the file. Please try again.', isError: true },
          ]);
          setIsLoading(false);
          return;
        }
      }

      // Use the new short-chatbot API endpoint
      const response = await fetch(`${process.env.REACT_APP_ZOOMEY_API_URL || ''}/api/short-chatbot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('access_token')}`,
        },
        body: JSON.stringify({
          userId: effectiveUserId,
          type: chatType,
          message: messageText,
          sessionId: sessionId,
          categoryId: contextId ? parseInt(contextId, 10) : null,
          attachmentUrl: attachmentUrl,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Get session ID from response headers
      const newSessionId = response.headers.get('X-Session-Id');
      if (newSessionId) {
        setSessionId(newSessionId);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let assistantMessageIndex = null;

      console.log('[TaskAssistantChat] Starting to read stream...');

      // Reset status message
      setStatusMessage('Thinking');
      let statusTimeout = null;

      // Add placeholder for assistant message
      setMessages(prev => {
        assistantMessageIndex = prev.length;
        return [...prev, { role: 'assistant', content: '', isStreaming: true }];
      });

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('[TaskAssistantChat] Stream complete, total response length:', assistantMessage.length);
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.trim()) continue;

          // Handle Vercel AI SDK format: 0:"text"
          if (line.startsWith('0:')) {
            try {
              const text = JSON.parse(line.slice(2));
              if (typeof text === 'string') {
                assistantMessage += text;
                // Update status to Writing when receiving text
                setStatusMessage('Writing');
                if (statusTimeout) clearTimeout(statusTimeout);
                statusTimeout = setTimeout(() => setStatusMessage('Thinking'), 3000);
                
                setMessages(prev => {
                  const updated = [...prev];
                  if (assistantMessageIndex !== null && updated[assistantMessageIndex]) {
                    updated[assistantMessageIndex] = {
                      ...updated[assistantMessageIndex],
                      content: assistantMessage,
                    };
                  }
                  return updated;
                });
              }
            } catch {
              // Skip malformed JSON
            }
          }
          // Handle SSE format: data: {...}
          else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              // Handle tool status messages
              if (data.type === 'data-tool-status' && data.data?.status) {
                setStatusMessage(data.data.status);
                if (statusTimeout) clearTimeout(statusTimeout);
                statusTimeout = setTimeout(() => setStatusMessage('Thinking'), 5000);
              }
              // Handle text-delta
              else if (data.type === 'text-delta' && data.delta) {
                assistantMessage += data.delta;
                setStatusMessage('Writing');
                if (statusTimeout) clearTimeout(statusTimeout);
                statusTimeout = setTimeout(() => setStatusMessage('Thinking'), 3000);
                
                setMessages(prev => {
                  const updated = [...prev];
                  if (assistantMessageIndex !== null && updated[assistantMessageIndex]) {
                    updated[assistantMessageIndex] = {
                      ...updated[assistantMessageIndex],
                      content: assistantMessage,
                    };
                  }
                  return updated;
                });
              }
            } catch {
              // Skip malformed JSON
            }
          }
          // Handle error messages
          else if (line.startsWith('3:')) {
            try {
              const errorData = JSON.parse(line.slice(2));
              console.error('[TaskAssistantChat] Stream error:', errorData);
            } catch {
              // Skip
            }
          }
        }
      }

      // Clear status timeout
      if (statusTimeout) clearTimeout(statusTimeout);
      setStatusMessage('Thinking');

      // Mark streaming complete
      setMessages(prev => {
        const updated = [...prev];
        if (assistantMessageIndex !== null && updated[assistantMessageIndex]) {
          updated[assistantMessageIndex] = {
            ...updated[assistantMessageIndex],
            isStreaming: false,
          };
        }
        return updated;
      });

      // Notify parent to refresh items
      if (onItemsUpdated) {
        onItemsUpdated();
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[TaskAssistantChat] Request aborted');
      } else {
        console.error('[TaskAssistantChat] Error:', error);
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again.', isError: true },
        ]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [currentInput, currentLoading, effectiveUserId, chatType, contextId, sessionId, onItemsUpdated, onExternalSend, setCurrentInput, selectedFile, filePreview]);

  // Expose handleSend for external use
  const triggerSend = () => handleSend();

  // Clear chat history
  const handleClearChat = async () => {
    // Clear session in database
    if (sessionId) {
      try {
        await clearShortChatSession(sessionId, effectiveUserId);
        console.log('[TaskAssistantChat] Session cleared:', sessionId);
      } catch (error) {
        console.error('[TaskAssistantChat] Error clearing session:', error);
      }
    }

    // Reset state
    setMessages([
      { role: 'assistant', content: `Hello! I can help you manage your ${typeLabel}. What would you like to do?` }
    ]);
    setSessionId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Copy message to clipboard
  const handleCopyMessage = (text) => {
    // Remove HTML tags, tool context, and attachment markers for plain text copy
    const plainText = stripToolContext(text)
      .replace(/<[^>]*>/g, '')
      .replace(/\[Attachment:[^\]]*\]/g, '')
      .trim();
    navigator.clipboard.writeText(plainText).then(() => {
      // Could show a toast notification here
      console.log('[TaskAssistantChat] Copied to clipboard');
    }).catch(err => {
      console.error('[TaskAssistantChat] Failed to copy:', err);
    });
  };

  // Edit message - put it back in the input
  const handleEditMessage = (text) => {
    // Remove attachment markers and HTML
    const cleanText = text.replace(/<[^>]*>/g, '').replace(/\[Attachment:[^\]]*\]/g, '').replace(/\[📎[^\]]*\]/g, '').trim();
    setCurrentInput(cleanText);
    inputRef.current?.focus();
  };

  // Expose methods to parent via ref for external input control
  useImperativeHandle(ref, () => ({
    triggerSend: handleSend,
    setInputValue: (value) => setCurrentInput(value),
    selectFile: (file) => {
      if (!file) return;
      const validTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'text/csv', 'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      if (!validTypes.includes(file.type)) {
        alert('Please select an image (JPG, PNG, GIF, WEBP), PDF, or CSV/Excel file.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview({ type: 'image', url: reader.result, name: file.name });
        };
        reader.readAsDataURL(file);
      } else {
        const icon = file.type === 'application/pdf' ? 'bi-file-pdf' : 'bi-file-earmark-spreadsheet';
        setFilePreview({ type: 'file', icon, name: file.name });
      }
    },
    removeFile: handleRemoveFile,
    clearChat: handleClearChat,
    getInputRef: () => inputRef,
    isLoading: currentLoading,
    isLoadingSession: isLoadingSession,
    inputValue: currentInput,
    filePreview: filePreview,
    selectedFile: selectedFile,
    sessionId: sessionId
  }), [handleSend, setCurrentInput, currentLoading, isLoadingSession, currentInput, filePreview, selectedFile, handleRemoveFile, handleClearChat, sessionId]);

  return (
    <>
      <style>{`
        .task-assistant-container {
          font-family: 'DM Sans', system-ui, sans-serif;
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          background: var(--chatbot-bg, #FBF9FA);
          overflow: visible;
        }

        .task-assistant-container.minimized {
          width: 0px !important;
          min-width: 0px;
          background: transparent;
          overflow: visible;
        }

        .task-assistant-container.maximized {
          width: 100%;
          overflow: visible;
        }

        /* Toggle Button - Position at RIGHT edge, floating outside */
        .toggle-btn {
          position: absolute;
          top: 50%;
          right: -24px;
          transform: translateY(-50%);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--teal-bg, #14b8a6);
          border: none;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 1060;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.35);
        }

        .toggle-btn:hover {
          transform: translateY(-50%) scale(1.1);
          background: #0d9488;
          box-shadow: 0 6px 16px rgba(20, 184, 166, 0.45);
        }

        .toggle-btn i {
          font-size: 1rem;
          transition: transform 0.3s ease;
        }

        /* Hide toggle button when minimized - show minimized icon instead */
        .minimized .toggle-btn {
          display: none;
        }

        /* Minimized State - Show chat icon OUTSIDE modal on right */
        .minimized-view {
          position: absolute;
          right: -24px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          z-index: 1060;
        }

        .minimized-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--teal-bg, #14b8a6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.35);
          transition: all 0.3s ease;
        }

        .minimized-icon:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(20, 184, 166, 0.45);
        }

        .minimized-text {
          display: none;
        }

        /* Maximized View */
        .maximized-view {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          opacity: 1;
          transition: opacity 0.3s ease 0.1s;
          overflow: hidden;
        }

        .minimized .maximized-view {
          opacity: 0;
          pointer-events: none;
          position: absolute;
        }

        /* Header */
        .chat-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--background, #fff);
          border-bottom: 1px solid var(--border-color, #e2e8f0);
        }

        .chat-header-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--teal-bg, #14b8a6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
        }

        .chat-header-text {
          flex: 1;
        }

        .chat-header-text h4 {
          margin: 0;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text, #1e293b);
        }

        .chat-header-text span {
          font-size: 0.65rem;
          color: var(--text, #64748b);
        }

        .clear-chat-btn {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: var(--text, #94a3b8);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .clear-chat-btn:hover {
          background: #fee2e2;
          color: #ef4444;
        }

        .clear-chat-btn i {
          font-size: 0.8rem;
        }

        /* Messages Area */
        .messages-container {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 16px;
          background: var(--chatbot-bg, #FBF9FA);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .messages-container::-webkit-scrollbar {
          width: 4px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: var(--border-color, #e2e8f0);
          border-radius: 4px;
        }

        /* Message Bubbles */
        .message {
          display: flex;
          flex-direction: column;
          max-width: 85%;
          animation: messageSlide 0.3s ease;
        }

        @keyframes messageSlide {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .message.user {
          align-self: flex-end;
        }

        .message.assistant {
          align-self: flex-start;
        }

        .message-bubble {
          padding: 10px 14px;
          font-size: 0.8rem;
          line-height: 1.5;
          word-wrap: break-word;
          position: relative;
        }

        .message.user .message-bubble {
          background: linear-gradient(135deg, var(--teal-bg, #14b8a6), #0d9488);
          color: white;
          border-radius: 16px 16px 4px 16px;
          box-shadow: 0 2px 8px rgba(20, 184, 166, 0.2);
        }

        .message.assistant .message-bubble {
          background: var(--background, #fff);
          color: var(--text, #1e293b);
          border-radius: 16px 16px 16px 4px;
          border: 1px solid var(--border-color, #e2e8f0);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        /* Markdown-like formatting for assistant messages */
        .message.assistant .message-bubble strong {
          font-weight: 600;
          color: var(--text, #1e293b);
        }

        .message.assistant .message-bubble ul,
        .message.assistant .message-bubble ol {
          margin: 8px 0;
          padding-left: 20px;
        }

        .message.assistant .message-bubble li {
          margin: 4px 0;
          line-height: 1.4;
        }

        .message.assistant .message-bubble p {
          margin: 6px 0;
        }

        .message.assistant .message-bubble code {
          background: var(--border-color, #f1f5f9);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.75rem;
        }

        .message-time {
          font-size: 0.6rem;
          color: var(--text, #64748b);
          opacity: 0.6;
          margin-top: 4px;
          padding: 0 4px;
        }

        .message.user .message-time {
          text-align: right;
        }

        /* Assistant message header with avatar */
        .assistant-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .assistant-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #f97316;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .assistant-name {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text, #1e293b);
        }

        /* Loading Indicator */
        .loading-indicator {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 0;
          background: transparent;
          border-radius: 0;
          border: none;
          max-width: 240px;
          animation: messageSlide 0.3s ease;
        }

        .loading-content {
          display: flex;
          flex-direction: column;
        }

        .thinking-text {
          font-size: 0.85rem;
          color: #9ca3af;
          font-style: italic;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .thinking-text .dots {
          display: inline-flex;
          gap: 2px;
        }

        .thinking-text .dots span {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #9ca3af;
          animation: thinkingDot 1.4s infinite ease-in-out;
        }

        .thinking-text .dots span:nth-child(1) { animation-delay: 0s; }
        .thinking-text .dots span:nth-child(2) { animation-delay: 0.2s; }
        .thinking-text .dots span:nth-child(3) { animation-delay: 0.4s; }

        @keyframes thinkingDot {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }

        .loading-time {
          font-size: 0.7rem;
          color: #9ca3af;
          margin-top: 2px;
        }

        .typing-dots {
          display: flex;
          gap: 4px;
        }

        .typing-dots span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--teal-bg, #14b8a6);
          animation: typingBounce 1.4s infinite ease-in-out;
        }

        .typing-dots span:nth-child(1) { animation-delay: -0.32s; }
        .typing-dots span:nth-child(2) { animation-delay: -0.16s; }
        .typing-dots span:nth-child(3) { animation-delay: 0s; }

        @keyframes typingBounce {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Input Area - Contained within chat */
        .input-container {
          flex-shrink: 0;
          padding: 12px 16px;
          background: var(--chatbot-bg, #FBF9FA);
          border-top: 1px solid var(--border-color, #e2e8f0);
        }

        .input-wrapper {
          display: flex;
          gap: 8px;
          align-items: center;
          background: var(--background, #fff);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 24px;
          padding: 4px 4px 4px 16px;
          transition: all 0.2s ease;
        }

        .input-wrapper:focus-within {
          border-color: var(--teal-bg, #14b8a6);
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
        }

        .input-wrapper input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 0.8rem;
          font-family: inherit;
          color: var(--text, #1e293b);
          outline: none;
          padding: 8px 0;
        }

        .input-wrapper input::placeholder {
          color: var(--text, #94a3b8);
          opacity: 0.6;
        }

        .send-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: var(--teal-bg, #14b8a6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .send-btn:hover:not(:disabled) {
          background: #0d9488;
          transform: scale(1.05);
        }

        .send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .send-btn i {
          font-size: 0.75rem;
          transform: translateX(1px);
        }

        /* Attachment button */
        .attach-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid var(--border-color, #e2e8f0);
          background: transparent;
          color: var(--text, #64748b);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .attach-btn:hover:not(:disabled) {
          border-color: var(--teal-bg, #14b8a6);
          color: var(--teal-bg, #14b8a6);
        }

        .attach-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .attach-btn i {
          font-size: 0.85rem;
        }

        /* File Preview */
        .file-preview-container {
          padding: 8px 16px;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
          background: var(--chatbot-bg, #f8fafc);
        }

        .file-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: var(--background, #fff);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 8px;
        }

        .file-preview-image {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          object-fit: cover;
        }

        .file-preview-icon {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          background: var(--teal-bg, #14b8a6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .file-preview-info {
          flex: 1;
          min-width: 0;
        }

        .file-preview-name {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text, #1e293b);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-preview-type {
          font-size: 0.65rem;
          color: var(--text, #64748b);
        }

        .file-preview-remove {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: none;
          background: #fee2e2;
          color: #ef4444;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .file-preview-remove:hover {
          background: #fecaca;
        }

        /* Message action icons */
        .message-actions {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .message:hover .message-actions {
          opacity: 1;
        }

        .message-action-btn {
          width: 24px;
          height: 24px;
          border: none;
          background: rgba(255,255,255,0.3);
          border-radius: 4px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          transition: all 0.2s ease;
        }

        .message-action-btn:hover {
          background: rgba(255,255,255,0.5);
        }

        .message.assistant .message-action-btn {
          background: rgba(0,0,0,0.05);
          color: #64748b;
        }

        .message.assistant .message-action-btn:hover {
          background: rgba(0,0,0,0.1);
          color: #1e293b;
        }

        .message-bubble-wrapper {
          position: relative;
        }

        /* User message with file */
        .message.user .message-file {
          margin-bottom: 6px;
        }

        .message.user .message-file img {
          max-width: 150px;
          max-height: 100px;
          border-radius: 8px;
        }

        .message.user .message-file-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(255,255,255,0.2);
          border-radius: 4px;
          font-size: 0.7rem;
        }

        /* User attachment preview in message */
        .user-attachment {
          margin-bottom: 8px;
        }

        .user-attachment-image {
          max-width: 200px;
          max-height: 150px;
          border-radius: 8px;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .user-attachment-image:hover {
          transform: scale(1.02);
        }

        .user-attachment-file {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(255,255,255,0.15);
          border-radius: 8px;
        }

        .user-attachment-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
        }

        .user-attachment-info {
          flex: 1;
          min-width: 0;
        }

        .user-attachment-name {
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }

        .user-attachment-type {
          font-size: 0.65rem;
          opacity: 0.8;
        }

        .user-message-text {
          margin-top: 4px;
        }

        /* Quick Actions - Hidden */
        .quick-actions {
          display: none;
        }

        .quick-action-btn {
          display: none;
        }
      `}</style>

      <div className={`task-assistant-container ${isMinimized ? 'minimized' : 'maximized'}`}>
        {/* Toggle Button */}
        <button
          className="toggle-btn"
          onClick={handleToggle}
          title={isMinimized ? 'Expand chat' : 'Minimize chat'}
        >
          <i className={`bi bi-chevron-${isMinimized ? 'left' : 'right'}`}></i>
        </button>

        {/* Minimized View - Click to expand */}
        {isMinimized && (
          <div className="minimized-view" onClick={handleToggle} title="Open chat">
            <div className="minimized-icon">
              <i className="bi bi-chat-dots-fill"></i>
            </div>
          </div>
        )}

        {/* Maximized View */}
        <div className="maximized-view" style={{ display: isMinimized ? 'none' : 'flex' }}>
          {/* Messages */}
          <div className="messages-container">
            {isLoadingSession ? (
              <div className="loading-indicator" style={{ alignSelf: 'center', marginTop: '20px' }}>
                <Spinner animation="border" size="sm" style={{ color: '#14b8a6' }} />
                <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#64748b' }}>Loading chat history...</span>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => {
                  
                  const parsed = msg.role === 'user' ? parseUserMessage(msg.content) : null;
                  const fileType = parsed?.attachmentUrl ? getFileTypeFromUrl(parsed.attachmentUrl) : null;
                  
                  // Check for local file preview (for newly sent messages) or parsed attachment URL (from DB)
                  const hasLocalFile = msg.file && (msg.file.url || msg.file.type === 'file');
                  const supabaseUrl = msg.attachmentUrl || parsed?.attachmentUrl;
                  const hasAttachment = hasLocalFile || supabaseUrl;
                  
                  // Prefer Supabase URL for opening (more reliable than data URL)
                  // Use local preview for display (faster), Supabase URL for clicks
                  const imageDisplayUrl = (hasLocalFile && msg.file?.url) || supabaseUrl;
                  const imageClickUrl = supabaseUrl || (hasLocalFile ? msg.file?.url : null);
                  
                  // Clean the display text (remove [📎 filename] pattern)
                  const displayText = msg.role === 'user' 
                    ? (parsed?.text || msg.content || '').replace(/\[📎[^\]]*\]/g, '').trim()
                    : msg.content;
                  
                  return (
                    <div key={idx} className={`message ${msg.role}`}>
                      {msg.role === 'assistant' && (
                        <div className="assistant-header">
                          <div className="assistant-avatar">Z</div>
                          <span className="assistant-name">Zoomey</span>
                        </div>
                      )}
                      <div className="message-bubble">
                        {msg.role === 'user' && hasAttachment ? (
                          <>
                            <div className="user-attachment">
                              {(hasLocalFile && msg.file?.type === 'image') || fileType === 'image' ? (
                                <img 
                                  src={imageDisplayUrl} 
                                  alt="Attachment" 
                                  className="user-attachment-image"
                                  onClick={() => imageClickUrl && window.open(imageClickUrl, '_blank')}
                                />
                              ) : (
                                <div className="user-attachment-file" onClick={() => imageClickUrl && window.open(imageClickUrl, '_blank')} style={{ cursor: 'pointer' }}>
                                  <div className="user-attachment-icon">
                                    <i className={`bi ${(hasLocalFile && msg.file?.icon) || (fileType === 'pdf' ? 'bi-file-pdf' : 'bi-file-earmark')}`}></i>
                                  </div>
                                  <div className="user-attachment-info">
                                    <div className="user-attachment-name">
                                      {hasLocalFile ? msg.file.name : (fileType === 'pdf' ? 'PDF Document' : 'File')}
                                    </div>
                                    <div className="user-attachment-type">
                                      {hasLocalFile ? 'Document' : fileType?.toUpperCase()}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            {displayText && <div className="user-message-text">{displayText}</div>}
                          </>
                        ) : msg.role === 'assistant' && msg.isStreaming && !msg.content ? (
                          <span className="thinking-text">
                            {statusMessage}
                            <span className="dots">
                              <span></span>
                              <span></span>
                              <span></span>
                            </span>
                          </span>
                        ) : (
                          <span dangerouslySetInnerHTML={{
                            __html: msg.role === 'assistant'
                              ? formatMessageContent(msg.content)
                              : displayText
                          }} />
                        )}
                        {/* Action icons - show on hover */}
                        {msg.content && !msg.isStreaming && (
                          <div className="message-actions">
                            <button
                              className="message-action-btn"
                              onClick={() => handleEditMessage(msg.content)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button
                              className="message-action-btn"
                              onClick={() => handleCopyMessage(msg.content)}
                              title="Copy"
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      <span className="message-time">
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  );
                })}

              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* File Preview - show above input when file is selected */}
          {filePreview && (
            <div className="file-preview-container">
              <div className="file-preview">
                {filePreview.type === 'image' ? (
                  <img src={filePreview.url} alt={filePreview.name} className="file-preview-image" />
                ) : (
                  <div className="file-preview-icon">
                    <i className={`bi ${filePreview.icon}`}></i>
                  </div>
                )}
                <div className="file-preview-info">
                  <div className="file-preview-name">{filePreview.name}</div>
                  <div className="file-preview-type">
                    {filePreview.type === 'image' ? 'Image' : 'Document'}
                  </div>
                </div>
                <button 
                  className="file-preview-remove" 
                  onClick={handleRemoveFile}
                  title="Remove file"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,.pdf,.csv,.xlsx,.xls"
            style={{ display: 'none' }}
          />

          {/* Input - only show if showInput is true */}
          {showInput && (
            <div className="input-container">
              <div className="input-wrapper">
                <button
                  className="attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={currentLoading || filePreview !== null}
                  title="Attach file"
                >
                  <i className="bi bi-paperclip"></i>
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={filePreview ? "Add context or send file..." : "What can I help you complete right now?"}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={currentLoading}
                />
                <button
                  className="send-btn"
                  onClick={handleSend}
                  disabled={(!currentInput.trim() && !selectedFile) || currentLoading}
                >
                  <i className="bi bi-send-fill"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default TaskAssistantChat;
